import type { Hex } from "@/color/oklch";
import type { PaletteKey } from "@/model/palette";
import { SCHEMA_URL, SCHEMA_VERSION, type SyntaxStyle, type Theme } from "@/model/theme";
import type { Ref, SyntaxKey, TokenId, Variant } from "@/model/tokens";

interface Starter {
  id: string;
  name: string;
  variant: Variant;
  credit: string;
  palette: Record<PaletteKey, Hex>;
  overrides?: Partial<Record<TokenId, Ref>>;
  styles?: Partial<Record<SyntaxKey, SyntaxStyle>>;
}

/**
 * Starting points based on popular open-source palettes (see LICENSES.md). They are
 * approximations mapped onto this app's model, not official ports.
 */
export const STARTERS: Starter[] = [
  {
    id: "gruvbox-dark",
    name: "Gruvbox Dark",
    variant: "dark",
    credit: "gruvbox by Pavel Pertsev (MIT)",
    palette: {
      bg0: "#1d2021",
      bg1: "#282828",
      bg2: "#3c3836",
      bg3: "#504945",
      bg4: "#665c54",
      fg3: "#928374",
      fg2: "#a89984",
      fg1: "#ebdbb2",
      fg0: "#fbf1c7",
      red: "#fb4934",
      orange: "#fe8019",
      yellow: "#fabd2f",
      green: "#b8bb26",
      cyan: "#8ec07c",
      blue: "#83a598",
      purple: "#d3869b",
      pink: "#d3869b",
      brightRed: "#fb4934",
      brightGreen: "#b8bb26",
      brightYellow: "#fabd2f",
      brightBlue: "#83a598",
      brightPurple: "#d3869b",
      brightCyan: "#8ec07c",
    },
    overrides: {
      "terminal.ansi0": "#282828",
      "terminal.ansi1": "#cc241d",
      "terminal.ansi2": "#98971a",
      "terminal.ansi3": "#d79921",
      "terminal.ansi4": "#458588",
      "terminal.ansi5": "#b16286",
      "terminal.ansi6": "#689d6a",
      "terminal.ansi15": "$fg1",
      "terminal.selectionBg": "$bg4",
      "ui.lineNrActive": "$yellow",
      // Gruvbox's ANSI black equals the background, so WeeChat bars use a neutral instead.
      "weechat.weechat.bar:status.color_bg": "$bg3",
      "weechat.weechat.bar:title.color_bg": "$bg3",
      "weechat.buflist:current_bg": "$bg3",
      "syntax.keyword": "$red",
      "syntax.control": "$red",
      "syntax.function": "$green",
      "syntax.method": "$green",
      "syntax.number": "$purple",
      "syntax.constant": "$purple",
      "syntax.boolean": "$purple",
      "syntax.builtin": "$orange",
      "syntax.property": "$blue",
      "syntax.parameter": "$blue",
      "syntax.operator": "$orange",
      "syntax.namespace": "$blue",
      "syntax.tag": "$cyan",
      "syntax.macro": "$cyan",
      "syntax.escape": "$orange",
      "syntax.heading": "$green",
    },
    styles: { comment: { italic: true }, string: { italic: true }, function: { bold: true } },
  },
  {
    id: "catppuccin-mocha",
    name: "Catppuccin Mocha",
    variant: "dark",
    credit: "Catppuccin (MIT)",
    palette: {
      bg0: "#181825",
      bg1: "#1e1e2e",
      bg2: "#313244",
      bg3: "#45475a",
      bg4: "#585b70",
      fg3: "#9399b2",
      fg2: "#a6adc8",
      fg1: "#cdd6f4",
      fg0: "#cdd6f4",
      red: "#f38ba8",
      orange: "#fab387",
      yellow: "#f9e2af",
      green: "#a6e3a1",
      cyan: "#94e2d5",
      blue: "#89b4fa",
      purple: "#cba6f7",
      pink: "#f5c2e7",
      brightRed: "#f37799",
      brightGreen: "#89d88b",
      brightYellow: "#ebd391",
      brightBlue: "#74a8fc",
      brightPurple: "#f2aede",
      brightCyan: "#6bd7ca",
    },
    overrides: {
      "terminal.ansi5": "$pink",
      "terminal.ansi8": "$bg4",
      "terminal.ansi15": "#bac2de",
      "syntax.parameter": "#eba0ac",
      "syntax.property": "#b4befe",
      "syntax.operator": "#89dceb",
      "syntax.punctuation": "$fg3",
      "syntax.namespace": "#b4befe",
      "syntax.tag": "$blue",
      "ui.lineNr": "$bg3",
      "ui.lineNrActive": "#b4befe",
    },
  },
  {
    id: "tokyo-night",
    name: "Tokyo Night",
    variant: "dark",
    credit: "tokyonight.nvim by Folke Lemaitre (Apache-2.0)",
    palette: {
      bg0: "#16161e",
      bg1: "#1a1b26",
      bg2: "#292e42",
      bg3: "#283457",
      bg4: "#3b4261",
      fg3: "#565f89",
      fg2: "#a9b1d6",
      fg1: "#c0caf5",
      fg0: "#c0caf5",
      red: "#f7768e",
      orange: "#ff9e64",
      yellow: "#e0af68",
      green: "#9ece6a",
      cyan: "#7dcfff",
      blue: "#7aa2f7",
      purple: "#bb9af7",
      pink: "#ff007c",
      brightRed: "#ff899d",
      brightGreen: "#9fe044",
      brightYellow: "#faba4a",
      brightBlue: "#8db0ff",
      brightPurple: "#c7a9ff",
      brightCyan: "#a4daff",
    },
    overrides: {
      "terminal.ansi0": "#15161e",
      "terminal.ansi8": "#414868",
      "syntax.type": "#2ac3de",
      "syntax.parameter": "$yellow",
      "syntax.property": "#73daca",
      "syntax.operator": "#89ddff",
      "syntax.punctuation": "#89ddff",
      "syntax.builtin": "#2ac3de",
      "syntax.namespace": "$cyan",
      "syntax.escape": "$purple",
      "syntax.macro": "$cyan",
      "ui.lineNrActive": "#737aa2",
    },
    styles: { comment: { italic: true }, keyword: { italic: true } },
  },
  {
    id: "nord",
    name: "Nord",
    variant: "dark",
    credit: "Nord by Arctic Ice Studio (MIT)",
    palette: {
      bg0: "#242933",
      bg1: "#2e3440",
      bg2: "#3b4252",
      bg3: "#434c5e",
      bg4: "#4c566a",
      fg3: "#616e88",
      fg2: "#b4bac4",
      fg1: "#d8dee9",
      fg0: "#eceff4",
      red: "#bf616a",
      orange: "#d08770",
      yellow: "#ebcb8b",
      green: "#a3be8c",
      cyan: "#88c0d0",
      blue: "#81a1c1",
      purple: "#b48ead",
      pink: "#b48ead",
      brightRed: "#bf616a",
      brightGreen: "#a3be8c",
      brightYellow: "#ebcb8b",
      brightBlue: "#81a1c1",
      brightPurple: "#b48ead",
      brightCyan: "#8fbcbb",
    },
    overrides: {
      "terminal.ansi0": "$bg2",
      "terminal.ansi7": "#e5e9f0",
      "terminal.ansi8": "#596377",
      "syntax.keyword": "$blue",
      "syntax.control": "$blue",
      "syntax.function": "$cyan",
      "syntax.method": "$cyan",
      "syntax.number": "$purple",
      "syntax.constant": "$purple",
      "syntax.boolean": "$blue",
      "syntax.type": "#8fbcbb",
      "syntax.operator": "$blue",
      "syntax.builtin": "$blue",
      "syntax.property": "$fg1",
      "syntax.tag": "$blue",
      "syntax.attribute": "#8fbcbb",
      "syntax.namespace": "#8fbcbb",
      "syntax.macro": "$blue",
      "syntax.escape": "$yellow",
      "syntax.regex": "$yellow",
      "ui.accent": "$cyan",
    },
  },
  {
    id: "rose-pine",
    name: "Rosé Pine",
    variant: "dark",
    credit: "Rosé Pine (MIT)",
    palette: {
      bg0: "#16141f",
      bg1: "#191724",
      bg2: "#21202e",
      bg3: "#403d52",
      bg4: "#524f67",
      fg3: "#6e6a86",
      fg2: "#908caa",
      fg1: "#e0def4",
      fg0: "#e0def4",
      red: "#eb6f92",
      orange: "#ebbcba",
      yellow: "#f6c177",
      green: "#31748f",
      cyan: "#9ccfd8",
      blue: "#31748f",
      purple: "#c4a7e7",
      pink: "#ebbcba",
      brightRed: "#eb6f92",
      brightGreen: "#31748f",
      brightYellow: "#f6c177",
      brightBlue: "#9ccfd8",
      brightPurple: "#c4a7e7",
      brightCyan: "#ebbcba",
    },
    overrides: {
      "terminal.ansi0": "#26233a",
      "terminal.ansi4": "$cyan",
      "terminal.ansi6": "$pink",
      "terminal.ansi7": "$fg1",
      "syntax.keyword": "$green",
      "syntax.control": "$green",
      "syntax.function": "$pink",
      "syntax.method": "$pink",
      "syntax.string": "$yellow",
      "syntax.number": "$yellow",
      "syntax.constant": "$yellow",
      "syntax.boolean": "$pink",
      "syntax.type": "$cyan",
      "syntax.parameter": "$purple",
      "syntax.property": "$cyan",
      "syntax.operator": "$fg2",
      "syntax.tag": "$cyan",
      "syntax.builtin": "$red",
      "syntax.namespace": "$purple",
      "syntax.macro": "$purple",
      "syntax.heading": "$purple",
      "ui.accent": "$purple",
    },
    styles: { comment: { italic: true }, parameter: { italic: true } },
  },
  {
    id: "kanagawa-wave",
    name: "Kanagawa Wave",
    variant: "dark",
    credit: "kanagawa.nvim by Tommaso Laurenzi (MIT)",
    palette: {
      bg0: "#16161d",
      bg1: "#1f1f28",
      bg2: "#2a2a37",
      bg3: "#223249",
      bg4: "#54546d",
      fg3: "#727169",
      fg2: "#c8c093",
      fg1: "#dcd7ba",
      fg0: "#dcd7ba",
      red: "#e46876",
      orange: "#ffa066",
      yellow: "#e6c384",
      green: "#98bb6c",
      cyan: "#7aa89f",
      blue: "#7e9cd8",
      purple: "#957fb8",
      pink: "#d27e99",
      brightRed: "#e82424",
      brightGreen: "#98bb6c",
      brightYellow: "#e6c384",
      brightBlue: "#7fb4ca",
      brightPurple: "#938aa9",
      brightCyan: "#7aa89f",
    },
    overrides: {
      "terminal.ansi0": "$bg0",
      "terminal.ansi1": "#c34043",
      "terminal.ansi2": "#76946a",
      "terminal.ansi3": "#c0a36e",
      "terminal.ansi6": "#6a9589",
      "syntax.number": "$pink",
      "syntax.type": "$cyan",
      "syntax.parameter": "#b8b4d0",
      "syntax.property": "$yellow",
      "syntax.operator": "#c0a36e",
      "syntax.punctuation": "#9cabca",
      "syntax.macro": "$red",
      "syntax.namespace": "$yellow",
      "syntax.escape": "#ff5d62",
      "syntax.tag": "$yellow",
      "ui.lineNrActive": "#ff9e3b",
    },
    styles: { comment: { italic: true }, keyword: { italic: true } },
  },
  {
    id: "everforest-dark",
    name: "Everforest Dark",
    variant: "dark",
    credit: "everforest by sainnhe (MIT)",
    palette: {
      bg0: "#232a2e",
      bg1: "#2d353b",
      bg2: "#343f44",
      bg3: "#475258",
      bg4: "#4f585e",
      fg3: "#859289",
      fg2: "#9da9a0",
      fg1: "#d3c6aa",
      fg0: "#d3c6aa",
      red: "#e67e80",
      orange: "#e69875",
      yellow: "#dbbc7f",
      green: "#a7c080",
      cyan: "#83c092",
      blue: "#7fbbb3",
      purple: "#d699b6",
      pink: "#d699b6",
      brightRed: "#e67e80",
      brightGreen: "#a7c080",
      brightYellow: "#dbbc7f",
      brightBlue: "#7fbbb3",
      brightPurple: "#d699b6",
      brightCyan: "#83c092",
    },
    overrides: {
      "syntax.keyword": "$red",
      "syntax.control": "$red",
      "syntax.function": "$green",
      "syntax.method": "$green",
      "syntax.string": "$cyan",
      "syntax.number": "$purple",
      "syntax.constant": "$purple",
      "syntax.boolean": "$purple",
      "syntax.operator": "$orange",
      "syntax.property": "$blue",
      "syntax.builtin": "$orange",
      "syntax.macro": "$cyan",
      "ui.accent": "$green",
    },
  },
  {
    id: "dracula",
    name: "Dracula",
    variant: "dark",
    credit: "Dracula Theme (MIT)",
    palette: {
      bg0: "#21222c",
      bg1: "#282a36",
      bg2: "#343746",
      bg3: "#44475a",
      bg4: "#545772",
      fg3: "#6272a4",
      fg2: "#c6c6c2",
      fg1: "#f8f8f2",
      fg0: "#ffffff",
      red: "#ff5555",
      orange: "#ffb86c",
      yellow: "#f1fa8c",
      green: "#50fa7b",
      cyan: "#8be9fd",
      blue: "#bd93f9",
      purple: "#bd93f9",
      pink: "#ff79c6",
      brightRed: "#ff6e6e",
      brightGreen: "#69ff94",
      brightYellow: "#ffffa5",
      brightBlue: "#d6acff",
      brightPurple: "#ff92df",
      brightCyan: "#a4ffff",
    },
    overrides: {
      "terminal.ansi0": "$bg0",
      "terminal.ansi5": "$pink",
      "terminal.ansi7": "$fg1",
      "syntax.keyword": "$pink",
      "syntax.control": "$pink",
      "syntax.function": "$green",
      "syntax.method": "$green",
      "syntax.string": "$yellow",
      "syntax.number": "$purple",
      "syntax.constant": "$purple",
      "syntax.boolean": "$purple",
      "syntax.type": "$cyan",
      "syntax.builtin": "$cyan",
      "syntax.parameter": "$orange",
      "syntax.property": "$fg1",
      "syntax.operator": "$pink",
      "syntax.tag": "$pink",
      "syntax.attribute": "$green",
      "syntax.namespace": "$cyan",
      "syntax.macro": "$pink",
      "syntax.escape": "$pink",
      "syntax.regex": "$red",
      "syntax.heading": "$purple",
      "ui.accent": "$purple",
    },
    styles: { comment: { italic: true }, type: { italic: true }, parameter: { italic: true } },
  },
  {
    id: "solarized-dark",
    name: "Solarized Dark",
    variant: "dark",
    credit: "Solarized by Ethan Schoonover (MIT)",
    palette: {
      bg0: "#00212b",
      bg1: "#002b36",
      bg2: "#073642",
      bg3: "#0e4553",
      bg4: "#2b5a66",
      fg3: "#586e75",
      fg2: "#657b83",
      fg1: "#839496",
      fg0: "#93a1a1",
      red: "#dc322f",
      orange: "#cb4b16",
      yellow: "#b58900",
      green: "#859900",
      cyan: "#2aa198",
      blue: "#268bd2",
      purple: "#6c71c4",
      pink: "#d33682",
      brightRed: "#e35d5b",
      brightGreen: "#9db31a",
      brightYellow: "#d3a21a",
      brightBlue: "#4aa3e0",
      brightPurple: "#8a8ed4",
      brightCyan: "#3fbdb3",
    },
    overrides: {
      "terminal.ansi0": "$bg2",
      "terminal.ansi5": "$pink",
      "terminal.ansi7": "#eee8d5",
      "terminal.ansi15": "#fdf6e3",
      "syntax.keyword": "$green",
      "syntax.control": "$green",
      "syntax.string": "$cyan",
      "syntax.number": "$cyan",
      "syntax.constant": "$cyan",
      "syntax.boolean": "$cyan",
      "syntax.type": "$yellow",
      "syntax.variable": "$blue",
      "syntax.parameter": "$fg1",
      "syntax.property": "$blue",
      "syntax.operator": "$green",
      "syntax.builtin": "$orange",
      "syntax.macro": "$orange",
      "syntax.escape": "$red",
      "syntax.tag": "$blue",
    },
  },
  {
    id: "solarized-light",
    name: "Solarized Light",
    variant: "light",
    credit: "Solarized by Ethan Schoonover (MIT)",
    palette: {
      bg0: "#eee8d5",
      bg1: "#fdf6e3",
      bg2: "#f3ecd8",
      bg3: "#e4ddc8",
      bg4: "#d3cbb4",
      fg3: "#93a1a1",
      fg2: "#839496",
      fg1: "#657b83",
      fg0: "#586e75",
      red: "#dc322f",
      orange: "#cb4b16",
      yellow: "#b58900",
      green: "#859900",
      cyan: "#2aa198",
      blue: "#268bd2",
      purple: "#6c71c4",
      pink: "#d33682",
      brightRed: "#b52a27",
      brightGreen: "#6b7a00",
      brightYellow: "#916e00",
      brightBlue: "#1d6fa8",
      brightPurple: "#5458a8",
      brightCyan: "#20817a",
    },
    overrides: {
      "terminal.ansi0": "#073642",
      "terminal.ansi5": "$pink",
      "terminal.ansi7": "#eee8d5",
      "terminal.ansi8": "#002b36",
      "terminal.ansi15": "#fdf6e3",
      "syntax.keyword": "$green",
      "syntax.control": "$green",
      "syntax.string": "$cyan",
      "syntax.number": "$cyan",
      "syntax.constant": "$cyan",
      "syntax.boolean": "$cyan",
      "syntax.type": "$yellow",
      "syntax.variable": "$blue",
      "syntax.parameter": "$fg1",
      "syntax.property": "$blue",
      "syntax.operator": "$green",
      "syntax.builtin": "$orange",
      "syntax.macro": "$orange",
      "syntax.escape": "$red",
      "syntax.tag": "$blue",
    },
  },
];

const EPOCH = "2026-10-30T00:00:00.000Z";

export function starterTheme(id: string): Theme | undefined {
  const s = STARTERS.find((x) => x.id === id);
  if (!s) return undefined;
  return {
    $schema: SCHEMA_URL,
    version: SCHEMA_VERSION,
    id: `starter:${s.id}`,
    name: s.name,
    variant: s.variant,
    palette: { ...s.palette },
    overrides: { ...s.overrides },
    styles: structuredClone(s.styles ?? {}),
    locks: [],
    meta: { createdAt: EPOCH, updatedAt: EPOCH, basedOn: s.name },
  };
}

export const DEFAULT_STARTER = "gruvbox-dark";
